// v2 dead tree — a weather-torn leaning snag: two chained tapering trunk segments
// end in a jagged splintered break (a crooked spike plus a short shard), one
// surviving bough forks into clawed bare tips with a drooping claw beneath, a
// snapped-off blunt stub juts from the waist, root flares grip the ground, and
// the fallen limb lies broken in the dirt beside the base. Bleached pale wood
// with weathered-dark undersides. Variants re-deal the lean, the bough yaws and
// where the fallen limb dropped. Ported 1:1 from age-of-mcp's v2/dead-tree.ts,
// using the library's shared `tropic` (h01, lerp, tube) helpers.
import { defineModel, tropic, type Part, type Vec3 } from "../../lib";

const WOOD = "#a89e8c";
const WOOD_DK = "#867c6c";
const WOOD_PALE = "#c3b9a4";

function deadTreeParts(v: number): Part[] {
  const seed = v * 37 + 15;
  const la = v * 2.5 + tropic.h01(seed, 0) * 0.8;        // lean direction
  const lean = tropic.lerp(0.1, 0.2, tropic.h01(seed, 1));      // lean strength
  const lx = Math.cos(la) * lean, lz = Math.sin(la) * lean;
  const mid: Vec3 = [lx * 0.5, 0.56, lz * 0.5];
  const high: Vec3 = [lx, 1.02, lz];
  const parts: Part[] = [
    // two chained tapering trunk segments, bowing with the lean
    tropic.tube([0, 0, 0], mid, 0.105, 0.075, WOOD, 6),
    tropic.tube(mid, high, 0.075, 0.05, WOOD, 6),
    // the splintered break: a crooked spike and a short shard
    tropic.tube(high, [lx + Math.cos(la + 0.8) * 0.07, 1.26,
      lz + Math.sin(la + 0.8) * 0.07], 0.045, 0.004, WOOD_PALE, 4),
    tropic.tube(high, [lx + Math.cos(la + 2.6) * 0.09, 1.12,
      lz + Math.sin(la + 2.6) * 0.09], 0.03, 0.004, WOOD_PALE, 4),
  ];
  // The surviving bough, forking into clawed bare tips.
  const ba = la + 1.5 + tropic.h01(seed, 10) * 0.8;
  const bp: Vec3 = [lx * 0.75 + Math.cos(ba) * 0.3, 1.0,
    lz * 0.75 + Math.sin(ba) * 0.3];
  parts.push(tropic.tube([lx * 0.7, 0.8, lz * 0.7], bp, 0.042, 0.026, WOOD, 5));
  for (let f = 0; f < 2; f++) {
    const fa = ba + (f ? 0.5 : -0.55) + tropic.h01(seed, 20 + f) * 0.4;
    parts.push(tropic.tube(bp, [bp[0] + Math.cos(fa) * 0.22, f ? 1.2 : 1.12,
      bp[2] + Math.sin(fa) * 0.22], 0.02, 0.004, WOOD_DK, 4));
  }
  // ...and one claw drooping back under the bough.
  parts.push(tropic.tube(bp, [bp[0] + Math.cos(ba) * 0.18, 0.9,
    bp[2] + Math.sin(ba) * 0.18], 0.016, 0.003, WOOD_DK, 4));
  // Snapped-off stub at the waist — blunt, never tapering to a point.
  const sa = la - 1.6 + tropic.h01(seed, 30) * 0.7;
  parts.push(tropic.tube([lx * 0.45, 0.52, lz * 0.45],
    [lx * 0.45 + Math.cos(sa) * 0.17, 0.62, lz * 0.45 + Math.sin(sa) * 0.17],
    0.034, 0.022, WOOD_DK, 5));
  // Root flares gripping the ground.
  for (let i = 0; i < 3; i++) {
    const a = la + 0.7 + (i / 3) * Math.PI * 2 + tropic.h01(seed, 40 + i) * 0.5;
    parts.push(tropic.tube([Math.cos(a) * 0.06, 0.15, Math.sin(a) * 0.06],
      [Math.cos(a) * 0.24, 0, Math.sin(a) * 0.24], 0.03, 0.055, WOOD_DK, 4));
  }
  // The fallen limb, broken in the dirt beside the base.
  const fa = la + 2.4 + tropic.h01(seed, 50) * 0.9;
  const fx = Math.cos(fa) * 0.34, fz = Math.sin(fa) * 0.34;
  parts.push(tropic.tube(
    [fx - Math.cos(fa + 1.2) * 0.18, 0.035, fz - Math.sin(fa + 1.2) * 0.18],
    [fx + Math.cos(fa + 1.2) * 0.18, 0.05, fz + Math.sin(fa + 1.2) * 0.18],
    0.022, 0.034, WOOD_PALE, 4));
  return parts;
}

export const deadTreeV2 = defineModel({
  id: "dead-tree-v2",
  batchId: "fable",
  name: "Dead tree (v2)",
  tags: ["scenery","dead","tree","low-poly","age-of-mcp","fable"],
  summary: "Low-poly dead tree.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.4,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => deadTreeParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => deadTreeParts(1) },
    { id: "variant-2", label: "Variant 3", parts: () => deadTreeParts(2) },
  ],
  animations: [],
});
